import React, { useEffect, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

interface TimePickerProps {
    name: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    icon?: string;
    label?: string;

}


const ChevronDown = FiChevronDown as any;

/* ===== CONSTANTS ===== */
const ROW_HEIGHT = 32;
const VISIBLE_ROWS = 5;
const CENTER_INDEX = Math.floor(VISIBLE_ROWS / 2);

const hours = Array.from({ length: 12 }, (_, i) => i + 1);
const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
);
const periods = ["AM", "PM"] as const;

const TimePicker: React.FC<TimePickerProps> = ({
    value,
    onChange,
    error,
    icon,
    label,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [hour, setHour] = useState("9");
    const [minute, setMinute] = useState("15");
    const [period, setPeriod] = useState<"AM" | "PM">("AM");

    const hourRef = useRef<HTMLDivElement>(null);
    const minuteRef = useRef<HTMLDivElement>(null);

    /* ===== PARSE EXISTING VALUE ===== */
    useEffect(() => {
        if (!value) return;
        const [time, p] = value.split(" ");
        const [h, m] = time.split(":");

        setHour(h.replace(/^0/, ""));
        setMinute(m);
        setPeriod(p as "AM" | "PM");
    }, [value]);

    /* ===== AUTO SCROLL TO SELECTED ===== */
    useEffect(() => {
        if (!isOpen) return;

        const hIndex = hours.indexOf(Number(hour));
        const mIndex = minutes.indexOf(minute);

        if (hourRef.current && hIndex >= 0) {
            hourRef.current.scrollTop = hIndex * ROW_HEIGHT;
        }

        if (minuteRef.current && mIndex >= 0) {
            minuteRef.current.scrollTop = mIndex * ROW_HEIGHT;
        }
    }, [isOpen, hour, minute]);

    /* ===== CLOSE ON OUTSIDE CLICK ===== */
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    /* ===== UPDATE TIME ===== */
    const updateTime = (
        h = hour,
        m = minute,
        p = period
    ) => {
        const formatted = `${h.padStart(2, "0")}:${m} ${p}`;
        onChange(formatted);
    };

    return (
        <div className="relative w-full border-t-2 border-[#F5F6F7] border-x-[1px] " ref={wrapperRef}>
            {/* INPUT */}
            <div
                className="h-[48px] flex items-center cursor-pointer ps-3 rounded-md"
                onClick={() => setIsOpen(!isOpen)}
            >
                <p className="m-0 !text-[13px] 2xl:!text-[16px] !font-normal text-[#747474] w-20">{label}</p>
                {icon && (
                    <img src={icon} alt="icon" className="w-7 h-7 mr-1" />
                )}
                <span className="w-full !text-[13px] 2xl:!text-[16px] !font-normal text-[#747474]">
                    {value || "Add Time"}
                </span>
                <ChevronDown
                    className={`absolute right-3 transition-transform duration-200 pointer-events-none 
                        ${isOpen ? "rotate-180" : ""}`}
                    size={18}
                    color="#777"
                />
            </div>

            {/* DROPDOWN */}
            {isOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-lg z-20 py-1">
                    <div className="relative flex gap-4">

                        {/* 🔴 CENTER STATIC HIGHLIGHT */}
                        <div
                            className="absolute left-0 right-0 bg-[#F7F3FF] pointer-events-none -z-10"
                            style={{
                                top: CENTER_INDEX * ROW_HEIGHT,
                                height: ROW_HEIGHT,
                            }}
                        />

                        {/* HOURS */}
                        <div
                            ref={hourRef}
                            className="overflow-y-auto scroll-smooth no-scrollbar text-center ps-4"
                            style={{
                                height: ROW_HEIGHT * VISIBLE_ROWS,
                                paddingTop: ROW_HEIGHT * CENTER_INDEX,
                                paddingBottom: ROW_HEIGHT * CENTER_INDEX,
                            }}
                        >
                            {hours.map((h) => (
                                <div
                                    key={h}
                                    onClick={() => {
                                        setHour(h.toString());
                                        updateTime(h.toString());
                                    }}
                                    className="flex items-center justify-center text-[#676767] cursor-pointer"
                                    style={{ height: ROW_HEIGHT }}
                                >
                                    {h}
                                </div>
                            ))}
                        </div>

                        {/* DOT / COLON COLUMN */}
                        <div
                            className="flex flex-col items-center justify-center text-[#9CA3AF]"
                            style={{ height: ROW_HEIGHT * VISIBLE_ROWS, width: 16 }}
                        >
                            {Array.from({ length: VISIBLE_ROWS }).map((_, i) => (
                                <div
                                    key={i}
                                    className="flex items-center justify-center"
                                    style={{ height: ROW_HEIGHT }}
                                >
                                    {i === CENTER_INDEX ? (
                                        <span className="text-lg font-semibold leading-none">:</span>
                                    ) : (
                                        <div className="flex flex-col gap-[2px]">
                                            <span className="w-[3px] h-[3px] rounded-full bg-[#9CA3AF]" />
                                            <span className="w-[3px] h-[3px] rounded-full bg-[#9CA3AF]" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>


                        {/* MINUTES */}
                        <div
                            ref={minuteRef}
                            className="overflow-y-auto scroll-smooth no-scrollbar text-center"
                            style={{
                                height: ROW_HEIGHT * VISIBLE_ROWS,
                                paddingTop: ROW_HEIGHT * CENTER_INDEX,
                                paddingBottom: ROW_HEIGHT * CENTER_INDEX,
                            }}
                        >
                            {minutes.map((m) => (
                                <div
                                    key={m}
                                    onClick={() => {
                                        setMinute(m);
                                        updateTime(hour, m);
                                    }}
                                    className="flex items-center justify-center text-[#676767] cursor-pointer"
                                    style={{ height: ROW_HEIGHT }}
                                >
                                    {m}
                                </div>
                            ))}
                        </div>

                        {/* AM / PM */}
                        <div
                            className={`flex flex-col justify-center ml-2 text-center pr-4 ${period === "AM" ? "!mt-8" : "!mb-8"}`}
                        // style={{ height: ROW_HEIGHT * VISIBLE_ROWS }}
                        >
                            {periods.map((p) => (
                                <div
                                    key={p}
                                    onClick={() => {
                                        setPeriod(p);
                                        updateTime(hour, minute, p);
                                    }}
                                    className={`flex items-center justify-center cursor-pointer ${period === p ? "text-black font-medium" : "text-gray-400"
                                        }`}
                                    style={{ height: ROW_HEIGHT }}
                                >
                                    {p}
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            )}


            {error && (
                <p className="text-red-500 text-sm mt-1">{error}</p>
            )}
        </div>
    );
};

export default TimePicker;
